import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, History, Loader2, Pencil, Plus, UserCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { whatsappApi } from "@/services/whatsapp-api";
import { formatDateTime } from "@/utils/dateUtils";
import type {
  WhatsAppDiaDoResumo,
  WhatsAppOrigemResumo,
  WhatsAppResumoConversa,
  WhatsAppSurfaceResumo,
  WhatsAppVersaoResumo,
} from "@/types/whatsapp";
import { WhatsAppLinhaDoTempo } from "./WhatsAppLinhaDoTempo";
import { toUserError } from "@/lib/api-error";

function rotuloDaOrigem(versao: WhatsAppVersaoResumo): string {
  const rotulos: Record<WhatsAppOrigemResumo, string> = {
    rolante: "Atualizado pela IA",
    regerado: "Refeito pela IA",
    humano: `Corrigido por ${versao.autor_nome ?? "uma pessoa"}`,
  };
  return rotulos[versao.origem] ?? versao.origem;
}

function useInvalidarResumos() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ["whatsapp-resumo"] });
    queryClient.invalidateQueries({ queryKey: ["whatsapp-resumos"] });
    queryClient.invalidateQueries({ queryKey: ["whatsapp-versoes"] });
  };
}

function CorrigirResumoDialog({
  conversa,
  surface,
  open,
  onOpenChange,
}: {
  conversa: WhatsAppResumoConversa;
  surface: WhatsAppSurfaceResumo;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const invalidar = useInvalidarResumos();
  const [dias, setDias] = useState<WhatsAppDiaDoResumo[]>(conversa.historico ?? []);
  const [status, setStatus] = useState(conversa.ultimo_status ?? "");
  const [nota, setNota] = useState("");
  const [novaData, setNovaData] = useState("");

  const salvar = useMutation({
    mutationFn: () =>
      whatsappApi.corrigirResumo(conversa.resumo_id, {
        historico: dias,
        ultimo_status: status.trim() || null,
        nota: nota.trim() || null,
        surface,
      }),
    onSuccess: () => {
      toast({ title: "Resumo corrigido", description: "Sua versão passa a valer." });
      invalidar();
      onOpenChange(false);
    },
    onError: (error: Error) =>
      toast({ title: "Não deu certo", description: toUserError(error).description, variant: "destructive" }),
  });

  const alterarTexto = (indice: number, texto: string) =>
    setDias((atuais) =>
      atuais.map((dia, posicao) => (posicao === indice ? { ...dia, texto } : dia)),
    );

  const adicionarDia = () => {
    if (!novaData) return;
    setDias((atuais) =>
      [...atuais, { data: novaData, texto: "" }].sort((a, b) => a.data.localeCompare(b.data)),
    );
    setNovaData("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Corrigir resumo</DialogTitle>
          <DialogDescription>
            Edite o que cada dia diz. Deixe o texto vazio para tirar o dia. Sua versão passa a valer
            e a IA continua a partir dela quando chegarem mensagens novas.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {dias.map((dia, indice) => {
            const [ano, mes, diaDoMes] = dia.data.split("-");
            return (
              <div key={dia.data} className="flex gap-3">
                <span className="w-24 shrink-0 pt-2 text-sm tabular-nums text-muted-foreground">
                  {diaDoMes}/{mes}/{ano}
                </span>
                <Textarea
                  value={dia.texto}
                  rows={2}
                  onChange={(event) => alterarTexto(indice, event.target.value)}
                />
              </div>
            );
          })}
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={novaData}
              onChange={(event) => setNovaData(event.target.value)}
              className="w-44"
            />
            <Button
              variant="outline"
              size="sm"
              disabled={!novaData || dias.some((dia) => dia.data === novaData)}
              onClick={adicionarDia}
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              Adicionar dia
            </Button>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Status atual da conversa</label>
            <Textarea value={status} rows={2} onChange={(event) => setStatus(event.target.value)} />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">O que estava errado (opcional)</label>
            <Input
              value={nota}
              placeholder="Ex.: o valor combinado foi outro"
              onChange={(event) => setNota(event.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            disabled={salvar.isPending || dias.every((dia) => !dia.texto.trim())}
            onClick={() => salvar.mutate()}
          >
            {salvar.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Salvar correção
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VersoesDialog({
  conversationId,
  open,
  onOpenChange,
}: {
  conversationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [aberta, setAberta] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["whatsapp-versoes", conversationId],
    queryFn: () => whatsappApi.versoesDoResumo(conversationId),
    enabled: open,
  });
  const versoes = data?.versoes ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Versões do resumo</DialogTitle>
          <DialogDescription>
            Cada vez que a IA atualiza ou alguém corrige, fica uma versão. A mais nova é a que vale.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <ol className="relative space-y-4 border-l pl-5">
            {versoes.map((versao, indice) => {
              const expandida = aberta === versao.id || (aberta === null && indice === 0);
              return (
                <li key={versao.id} className="relative">
                  <span
                    className={`absolute left-[-26px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-background ${
                      versao.origem === "humano" ? "bg-emerald-600" : "bg-muted-foreground"
                    }`}
                  />
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium">{formatDateTime(versao.gerado_em)}</span>
                    <span className="text-muted-foreground">{rotuloDaOrigem(versao)}</span>
                    {indice === 0 && <Badge variant="secondary">Atual</Badge>}
                    {versao.conferido_em && (
                      <span className="flex items-center gap-1 text-xs text-emerald-700">
                        <UserCheck className="h-3 w-3" />
                        Conferido por {versao.conferido_por ?? "alguém"} em{" "}
                        {formatDateTime(versao.conferido_em)}
                      </span>
                    )}
                    {!expandida && (
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={() => setAberta(versao.id)}
                      >
                        Ver
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Mensagens até {formatDateTime(versao.mensagens_ate)}
                    {versao.prompt_version ? ` · prompt ${versao.prompt_version}` : ""}
                  </p>
                  {expandida && (
                    <div className="mt-2 space-y-2 rounded-md border bg-muted/20 p-3">
                      <WhatsAppLinhaDoTempo historico={versao.historico} resumo={versao.resumo} />
                      {versao.ultimo_status && (
                        <p className="border-t pt-2 text-sm">
                          <span className="font-medium">Status:</span> {versao.ultimo_status}
                        </p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * "Está certo", "Corrigir" e as versões do resumo. O gesto fica gravado com o
 * que a IA tinha escrito, e a correção vira a versão que vale.
 */
export function WhatsAppResumoAcoes({
  conversa,
  surface,
}: {
  conversa: WhatsAppResumoConversa;
  surface: WhatsAppSurfaceResumo;
}) {
  const { toast } = useToast();
  const invalidar = useInvalidarResumos();
  const [corrigindo, setCorrigindo] = useState(false);
  const [vendoVersoes, setVendoVersoes] = useState(false);

  const confirmar = useMutation({
    mutationFn: () => whatsappApi.confirmarResumo(conversa.resumo_id, surface),
    onSuccess: () => {
      toast({ title: "Obrigado! Resumo marcado como conferido." });
      invalidar();
    },
    onError: (error: Error) =>
      toast({ title: "Não deu certo", description: toUserError(error).description, variant: "destructive" }),
  });

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      {conversa.origem === "humano" && (
        <Badge variant="secondary" className="font-normal">
          Corrigido por uma pessoa
        </Badge>
      )}
      {conversa.conferido_em ? (
        <span className="flex items-center gap-1 text-emerald-700">
          <UserCheck className="h-3.5 w-3.5" />
          Conferido por {conversa.conferido_por ?? "alguém"} em{" "}
          {formatDateTime(conversa.conferido_em)}
        </span>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="h-7"
          disabled={confirmar.isPending}
          onClick={() => confirmar.mutate()}
        >
          {confirmar.isPending ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="mr-1 h-3.5 w-3.5" />
          )}
          Está certo
        </Button>
      )}
      <Button
        variant="outline"
        size="sm"
        className="h-7"
        disabled={!conversa.historico?.length}
        title={
          conversa.historico?.length
            ? undefined
            : "Este resumo é antigo. Use 'Refazer resumos' antes de corrigir."
        }
        onClick={() => setCorrigindo(true)}
      >
        <Pencil className="mr-1 h-3.5 w-3.5" />
        Corrigir
      </Button>
      {conversa.versoes > 1 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-muted-foreground"
          onClick={() => setVendoVersoes(true)}
        >
          <History className="mr-1 h-3.5 w-3.5" />
          Versões ({conversa.versoes})
        </Button>
      )}

      {corrigindo && (
        <CorrigirResumoDialog
          conversa={conversa}
          surface={surface}
          open={corrigindo}
          onOpenChange={setCorrigindo}
        />
      )}
      <VersoesDialog
        conversationId={conversa.conversation_id}
        open={vendoVersoes}
        onOpenChange={setVendoVersoes}
      />
    </div>
  );
}
